import { memo } from 'react';

import { type Notification } from '@air/api/types';
import { type NotificationsItemProps } from '@air/component-notifications';
import { Check, Close } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { formatActorNameInitialsOrDefault, formatFullName } from '@air/utilities';

import { NotificationBaseItem } from '~/components/AirNotifications/components/NotificationBaseItem';
import { useLibraryApproveMemberMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberApproveMutation';
import { useLibraryMembersDenyMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberDenyMutation';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { convertUnknownToError } from '~/utils/ErrorUtils';

export type NotificationLibraryMemberRequestedTypeProps = Pick<NotificationsItemProps, 'onClick'> & {
  notification: Notification<'libraryMemberInvited_userIsAdmin', 'account'>;
  onApprove: () => void;
  onDeny: () => void;
};

export const NotificationLibraryMemberRequestedType = memo(
  ({ notification, onApprove, onClick, onDeny }: NotificationLibraryMemberRequestedTypeProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { showLibraryToast } = useLibraryToasts();
    const { libraryMembersDenyMutation } = useLibraryMembersDenyMutation({
      libraryId: notification.data.library.id,
    });
    const { libraryMemberApproveMutation } = useLibraryApproveMemberMutation({
      libraryId: notification.data.library.id,
    });

    /**
     * If the actor.id is not the same as the libraryMember.id, then we know that the
     * actor is inviting the libraryMember to the library.
     */
    const isMemberInvite = notification.data.actor.id !== notification.data.libraryMember.id;

    /**
     * Check if this is a pending request that needs approval or an informational notification
     * about a successful join (for open libraries)
     */
    const isPendingRequest = notification.data.libraryMember.pending;

    return (
      <NotificationBaseItem
        content={
          /**
           * We handle the showing and hiding of buttons based on the readAt property
-          * because we only mark the notifications as read when the user approves or
-          * denies the join request.
           * Only show approve/deny buttons for pending requests that haven't been read.
           * For non-pending (successful joins), don't show any action buttons.
           */
          isPendingRequest &&
          !notification.readAt && (
            <div className="relative z-10 flex gap-2">
              <Button
                appearance="outline"
                color="grey"
                isLoading={libraryMembersDenyMutation.isPending}
                onClick={() => {
                  const fullName = formatFullName(
                    notification.data.libraryMember.firstName,
                    notification.data.libraryMember.lastName,
                  );

                  libraryMembersDenyMutation.mutate([{ accountId: notification.data.libraryMember.id }], {
                    onError: (error) =>
                      showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                    onSuccess: () => {
                      onDeny?.();
                      showLibraryToast({
                        message: (
                          <>
                            Denied <b className="text-jay-200">{fullName}</b> join request.
                          </>
                        ),
                      });
                    },
                  });
                }}
                prefix={<Close className="size-3" />}
                size="small"
              >
                Deny
              </Button>
              <Button
                appearance="outline"
                color="grey"
                isLoading={libraryMemberApproveMutation.isPending}
                onClick={() => {
                  const fullName = formatFullName(
                    notification.data.libraryMember.firstName,
                    notification.data.libraryMember.lastName,
                  );

                  libraryMemberApproveMutation.mutate(
                    { accountId: notification.data.libraryMember.id },
                    {
                      onError: (error) =>
                        showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                      onSuccess: () => {
                        onApprove?.();
                        showLibraryToast({
                          message: (
                            <>
                              Approved <b className="text-jay-200">{fullName}</b> join request.
                            </>
                          ),
                        });
                      },
                    },
                  );
                }}
                prefix={<Check className="size-3" />}
                size="small"
              >
                Approve
              </Button>
            </div>
          )
        }
        meta={!!currentWorkspace?.name && `in ${currentWorkspace?.name}`}
        notification={notification}
        onClick={!!notification.readAt || !isPendingRequest ? onClick : undefined}
        title={
          <>
            <b>{formatActorNameInitialsOrDefault(notification.data.actor)}</b>

            {isPendingRequest ? (
              // Show request/invite language for pending requests
              isMemberInvite ? (
                <>
                  {` has invited `}
                  <b>
                    {formatActorNameInitialsOrDefault({
                      firstName: notification.data.libraryMember.firstName,
                      lastName: notification.data.libraryMember.lastName,
                    })}
                  </b>
                </>
              ) : (
                ` has requested`
              )
            ) : (
              // Show "joined" language for successful joins
              ` has joined`
            )}
            {` ${isPendingRequest ? 'to join' : ''} `}
            <b>{notification.data.library.title}</b>
          </>
        }
      />
    );
  },
);

NotificationLibraryMemberRequestedType.displayName = 'NotificationLibraryMemberRequestedType';
